import { useEffect, useRef } from 'react'
import { getToken } from '../services/token'
import { getRealtimeTicket } from '../services/api'

// Realtime WS hook — opens a WebSocket to /api/ws on mount, maintains it
// across renders, and dispatches every incoming event as a `realtime` window
// event that any component in the tree can listen for.  Handles auth
// (a one-time ticket in the URL — never the JWT), reconnect on close
// (exponential backoff), and graceful shutdown on unmount.
//
// Usage:
//   useRealtime()                       // mount once in Layout to keep the
//                                        // connection alive app-wide
//
//   useEffect(() => {                    // component-side listener
//     const h = (e) => {
//       if (e.detail.type === 'invoice.paid') showPaidToast(e.detail.payload)
//     }
//     window.addEventListener('realtime', h)
//     return () => window.removeEventListener('realtime', h)
//   }, [])
//
// Why window events instead of a React context: components that care about
// realtime events are scattered (Layout for toasts, MatterDetail for
// per-matter refresh, etc.); passing through context would require every
// intermediate to render an extra provider.  Native events are already
// global, decoupled from React tree, and cross-window in Electron.
//
// Auth (2026-10-21): a browser cannot put an Authorization header on a
// WebSocket handshake, and the old `/api/ws?token=<JWT>` wrote a 30-day bearer
// token into the web server's access log on every connect.  Now, right before
// EVERY connect and reconnect, the hook asks POST /api/ws/ticket (through
// services/api.js, so the JWT travels in the Authorization header) for a
// single-use, 30-second ticket and opens `/api/ws?ticket=<ticket>`.  A ticket
// is never reused: each reconnect fetches a fresh one.
//
// Reconnect strategy:
//   • Success = the server's `welcome` message (it is sent only after the
//     ticket and session checks pass).  It resets the attempt counter.  The
//     bare upgrade (onopen) is NOT success: the server upgrades first and
//     checks second, so a close right after an upgrade (1011, the session
//     check failed) must back off, not restart at 1 s every time.
//   • Ticket refused (401 signed out / 403 / 404 / any other 4xx) → stop,
//     exactly like the no-token path: no retry loop.  (A 401 also signs the
//     user out through api.js's interceptor, as on any other request.)
//   • Ticket unavailable (no response, 5xx, 408, 429) → normal backoff, so an
//     API restart doesn't end realtime for the session.  A 429 waits at least
//     its Retry-After (capped at MAX_RETRY_AFTER_MS): the per-user mint limit
//     resets each minute, so a browser restoring many tabs at once connects
//     the extra tabs a minute later instead of spending the 3 attempts inside
//     the limited window and giving up.
//   • 4401 at the handshake (no welcome on this socket) → the ticket may just
//     have gone stale (the API restarted between mint and connect, or the tab
//     was suspended for over 30 s).  Mint ONE fresh ticket: the mint is the
//     real auth check, so a revoked session gets 401 there and stops.  A
//     second handshake 4401 in a row is terminal; a welcome earns the retry
//     back.
//   • 4401 / 4403 after a welcome (session revoked or JWT expired while
//     connected) → stop trying (the user is signed out anyway).
//   • Failure before the socket was EVER welcomed (ticket unavailable, or the
//     WebSocket closed without a welcome) — likely the API is down or Apache
//     isn't routing wss:// to the API container.  Retry with exponential
//     backoff up to MAX_HANDSHAKE_FAILS attempts, then give up SILENTLY for
//     the rest of the session.  Without this cap the browser logged 5-6
//     WebSocket 404s every ~30s forever — noisy + useless.
//   • Welcome followed by a later close → normal backoff (server restart,
//     whatever) resumes trying.

const MAX_HANDSHAKE_FAILS = 3     // pre-welcome failures before we give up
const OPENED_FAILURE_CAP  = 30000 // once we've been welcomed at least once,
                                  // keep retrying up to this cap forever
const MAX_RETRY_AFTER_MS  = 120000 // never wait longer than this on a 429

// Is a failed ticket request worth retrying?  No HTTP answer at all (network
// down, API restarting), a 5xx, 408 or 429 is transient; any other status
// (401, 403, 404 — an API without the endpoint) will not change by retrying.
export function isTransientTicketFailure (err) {
  const status = err && err.response && err.response.status
  if (!status) return true
  return status >= 500 || status === 408 || status === 429
}

// A 429's Retry-After in ms (seconds form only; the API sends seconds), capped
// at MAX_RETRY_AFTER_MS.  0 when there is none.
export function retryAfterMs (err) {
  const res = err && err.response
  if (!res || res.status !== 429) return 0
  const headers = res.headers || {}
  const raw = typeof headers.get === 'function' ? headers.get('retry-after') : headers['retry-after']
  const secs = Number(raw)
  if (!Number.isFinite(secs) || secs <= 0) return 0
  return Math.min(secs * 1000, MAX_RETRY_AFTER_MS)
}

// The socket URL: ws:// or wss:// mirroring the page's protocol (no mixed-
// content warnings), the page's own host (Apache proxies /api/ws to the API
// container via mod_proxy_wstunnel), and ONLY the ticket in the query.
export function buildSocketUrl (ticket, loc = window.location) {
  const protocol = loc.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${loc.host}/api/ws?ticket=${encodeURIComponent(ticket)}`
}

export default function useRealtime({ enabled = true } = {}) {
  const wsRef = useRef(null)
  const reconnectTimerRef = useRef(null)
  const reconnectAttemptRef = useRef(0)
  const shouldReconnectRef = useRef(true)
  const everOpenedRef = useRef(false)
  const handshakeFailsRef = useRef(0)
  const warnedRef = useRef(false)
  // The one fresh-ticket retry after a handshake 4401; a welcome re-arms it.
  const staleRetryUsedRef = useRef(false)

  useEffect(() => {
    if (!enabled) return
    // Per-run flag: a ticket request still in flight when this effect is
    // cleaned up (unmount, or `enabled` flipping) must not open a socket —
    // the refs below are shared with the NEXT run of this effect.
    let cancelled = false
    shouldReconnectRef.current = true

    // A failure before the socket has ever been welcomed.  Returns true when
    // the cap is reached and realtime is off for the rest of the session.
    const notePreOpenFailure = () => {
      if (everOpenedRef.current) return false
      handshakeFailsRef.current += 1
      if (handshakeFailsRef.current < MAX_HANDSHAKE_FAILS) return false
      shouldReconnectRef.current = false
      if (!warnedRef.current) {
        warnedRef.current = true
        // Single one-shot warn — every-page 404 spam was the complaint that
        // motivated the cap.
        // eslint-disable-next-line no-console
        console.warn(
          '[realtime] could not connect after ' + MAX_HANDSHAKE_FAILS +
          ' attempts — realtime notifications disabled for this session.  ' +
          'If the WebSocket handshake is what fails, enable mod_proxy_wstunnel ' +
          'on the Apache vhost + add "ProxyPass /api/ws ws://localhost:3000/api/ws".'
        )
      }
      return true
    }

    const connect = async () => {
      if (cancelled || !shouldReconnectRef.current) return
      if (!getToken()) {
        // No token — user isn't logged in.  Don't attempt; retry when the
        // enabled effect re-fires post-login.
        return
      }

      // A fresh one-time ticket for THIS attempt (never reused).
      let ticket
      try {
        const res = await getRealtimeTicket()
        ticket = res && res.data && res.data.ticket
      } catch (err) {
        if (cancelled) return
        if (isTransientTicketFailure(err)) {
          if (!notePreOpenFailure()) scheduleReconnect(retryAfterMs(err))
        } else {
          // Signed out / refused — same as the no-token path above.
          shouldReconnectRef.current = false
        }
        return
      }
      if (cancelled || !shouldReconnectRef.current) return
      if (typeof ticket !== 'string' || !ticket) {
        shouldReconnectRef.current = false
        return
      }

      let ws
      try {
        ws = new WebSocket(buildSocketUrl(ticket))
      } catch {
        scheduleReconnect()
        return
      }
      wsRef.current = ws
      // Set by this socket's welcome — the server's "ticket and session OK".
      let welcomed = false

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data)
          // Server-sent pings are keepalives — reply pong.  Silent, no
          // window event.
          if (msg.type === 'ping') {
            try { ws.send(JSON.stringify({ type: 'pong', ts: Date.now() })) } catch {}
            return
          }
          // Welcome = the server accepted the ticket and the session.  This,
          // not onopen, is what resets the backoff.  Not propagated.
          if (msg.type === 'welcome') {
            welcomed = true
            reconnectAttemptRef.current = 0
            everOpenedRef.current = true
            handshakeFailsRef.current = 0
            staleRetryUsedRef.current = false
            return
          }
          // Everything else fans out as a window CustomEvent.
          window.dispatchEvent(new CustomEvent('realtime', { detail: msg }))
        } catch {
          // Malformed message — drop
        }
      }

      ws.onclose = (evt) => {
        if (wsRef.current === ws) wsRef.current = null
        if (cancelled) return
        if (evt.code === 4401 || evt.code === 4403) {
          // Refused at the handshake: possibly just a stale ticket.  One
          // fresh mint decides (a revoked session gets 401 there and stops).
          if (evt.code === 4401 && !welcomed && !staleRetryUsedRef.current) {
            staleRetryUsedRef.current = true
            if (notePreOpenFailure()) return
            scheduleReconnect()
            return
          }
          // Revoked / expired while connected, or refused twice in a row —
          // stop trying.
          shouldReconnectRef.current = false
          return
        }
        // Closed before we were EVER welcomed — most likely Apache's
        // mod_proxy_wstunnel isn't configured to route /api/ws, or the server
        // keeps failing its session check.  Counted with the pre-welcome
        // ticket failures; give up after MAX_HANDSHAKE_FAILS so the console
        // isn't flooded.
        if (notePreOpenFailure()) return
        scheduleReconnect()
      }

      ws.onerror = () => {
        // Let onclose handle the reconnect — onerror fires just before it.
      }
    }

    // minDelayMs: a floor from the server (a 429's Retry-After).
    const scheduleReconnect = (minDelayMs = 0) => {
      if (cancelled || !shouldReconnectRef.current) return
      const attempt = reconnectAttemptRef.current
      const delay = Math.max(Math.min(1000 * Math.pow(2, attempt), OPENED_FAILURE_CAP), minDelayMs)
      reconnectAttemptRef.current = attempt + 1
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = setTimeout(connect, delay)
    }

    connect()

    return () => {
      cancelled = true
      shouldReconnectRef.current = false
      clearTimeout(reconnectTimerRef.current)
      if (wsRef.current) {
        try { wsRef.current.close(1000, 'component unmount') } catch {}
        wsRef.current = null
      }
    }
  }, [enabled])
}
